import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import { ManagedDataSet, ManagedDataSetSecurity, ManagedDataSetInput, ManagedDataSetColumnFormat } from '../Shared/ManagedDataSet';
import { ManagedDrilldown } from '../Shared/ManagedDrilldown';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminOperation,
    ManagedAdminEditorMode,
    ManagedActionModalMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    mapDataSetAdminModel,
 } from '../Shared/ManagedHelpers';
import Administration from '../../Constants/Endpoints/Administration';
import GetDataSetAdminModelsWithChildrenRequest from '../../Interfaces/Requests/Administration/GetDataSetAdminModelsWithChildrenRequest';
import GetDataSetAdminModelsWithChildrenResponse from '../../Interfaces/Responses/Administration/GetDataSetAdminModelsWithChildrenResponse';
import AUDDataSetRequest from '../../Interfaces/Requests/Administration/AUDDataSetRequest';
import {
    AddIcon,
    Button,
    Card,
    Checkbox,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Modal as ServaModal,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import TextArea from 'antd/es/input/TextArea';
import { CloseCircleOutlined } from '@ant-design/icons';
import BulbOutlinedIlluminated from '../Shared/BulbOutlinedIlluminated';
import AIContextAssistModal from '../Shared/AIContextAssistModal';
import { extractAISQLAssistSuggestionText } from '../Shared/AISQLAssistUtils';
import GetAIProviderListModelRequest from '../../Interfaces/Requests/AI/GetAIProviderListModelRequest';
import GetAISingleTurnPromptModelRequest from '../../Interfaces/Requests/AI/GetAISingleTurnPromptModelRequest';
import GetAIDataSetExplainModelRequest from '../../Interfaces/Requests/AI/GetAIDataSetExplainModelRequest';
import GetAIProviderListModelResponse from '../../Interfaces/Responses/AI/GetAIProviderListModelResponse';
import GetAISingleTurnPromptModelResponse from '../../Interfaces/Responses/AI/GetAISingleTurnPromptModelResponse';
import GetAIExplainModelResponse from '../../Interfaces/Responses/AI/GetAIExplainModelResponse';
import AUDDataSetSecurities, { ManagedDataSetSecurityEditResult } from './AUDDataSetSecurities';
import AUDDataSetInputs, { ManagedDataSetInputEditResult } from './AUDDataSetInputs';
import AUDDataSetColumnFormats, { ManagedDataSetColumnFormatEditResult } from './AUDDataSetColumnFormats';
import AUDDrilldowns, { ManagedDrilldownEditResult } from './AUDDrilldowns';

type AUDDataSetProps = {};

type AUDDataSetFormValues = {
    dsDetailsName: string;
    dsDetailsNameGeneric: string;
    dsDetailsAllowFiltering: boolean;
    dsDetailsHideFromPortal: boolean;
    dsDetailsGroupBy: string;
    dsDetailsOrderBy: string;
    dsDetailsAlwaysShowFilters: boolean;
    dsDetailsInvolvesEncryption: boolean;
    dsDetailsGrouping: string;
    dsDetailsGroupingGeneric: string;
    dsDetailsIsolationLevel: boolean;
    dsDetailsQuery: string;
};

type AUDDataSetAIContext = {
    title: string;
    currentSectionTitle: string;
    requestSectionTitle: string;
    resultSectionTitle: string;
    requestPlaceholder: string;
    askButtonText: string;
    useButtonText: string;
    providerNamePhrase: string;
    targetTextDescription: string;
    allowUseSuggestion: boolean;
    assistMode: 'sqlAssist' | 'dataSetExplain';
};

const AUDDataSet: React.FC<AUDDataSetProps> = () => {
    const { user } = useAuth();
    const [formAUDDataSets] = Form.useForm();

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({ open: false, mode: 'info', title: '', message: '', isError: false, });

    const [addButtonClicked, setAddButtonClicked] = useState(true);
    const [updButtonClicked, setUpdButtonClicked] = useState(false);
    const [delButtonClicked, setDelButtonClicked] = useState(false);

    const [dsSelectedDataSetID, setDSSelectedDataSetID] = useState(-1);
    const [dsSelectedDataSetName, setDSSelectedDataSetName] = useState('');

    const [dsDetailsDataSetSecurities, setDSDetailsDataSetSecurities] = useState<ManagedDataSetSecurity[]>([]);
    const [dsDetailsDataSetInputs, setDSDetailsDataSetInputs] = useState<ManagedDataSetInput[]>([]);
    const [dsDetailsDataSetColumnFormats, setDSDetailsDataSetColumnFormats] = useState<ManagedDataSetColumnFormat[]>([]);
    const [dsDetailsDrilldowns, setDSDetailsDrilldowns] = useState<ManagedDrilldown[]>([]);

    const [manageDataSetSecuritiesModalOpen, setManageDataSetSecuritiesModalOpen] = useState(false);
    const [manageDataSetInputsModalOpen, setManageDataSetInputsModalOpen] = useState(false);
    const [manageDataSetColumnFormatsModalOpen, setManageDataSetColumnFormatsModalOpen] = useState(false);
    const [manageDataSetDrilldownsModalOpen, setManageDataSetDrilldownsModalOpen] = useState(false);

    const [aiContextAssistModalOpen, setAIContextAssistModalOpen] = useState(false);
    const [aiContextAssistCurrentText, setAIContextAssistCurrentText] = useState('');
    const [aiContextAssistResultText, setAIContextAssistResultText] = useState('');
    const [aiContextAssistAncillaryItems, setAIContextAssistAncillaryItems] = useState<string[]>([]);
    const [aiContextAssistLoading, setAIContextAssistLoading] = useState(false);

    const [aiContextAssistContext, setAIContextAssistContext] = useState<AUDDataSetAIContext>({
        title: 'AI DataSet Query Assist',
        currentSectionTitle: 'Current DataSet Query',
        requestSectionTitle: 'What do you want this DataSet query to do?',
        resultSectionTitle: 'Suggested DataSet Query',
        requestPlaceholder: 'Describe the DataSet query you want AI to help build or review.',
        askButtonText: 'Ask AI',
        useButtonText: 'Use Suggestion',
        providerNamePhrase: 'SQL Review',
        targetTextDescription: 'DataSet Query SQL text',
        allowUseSuggestion: true,
        assistMode: 'sqlAssist',
    });

    const [dsSelectedDataSetInputID, setDSSelectedDataSetInputID] = useState(-1);
    const [dsDataSetInputEditorMode, setDSDataSetInputEditorMode] = useState<ManagedAdminActionMode>('idle');
    const [dsSelectedDataSetSecurityID, setDSSelectedDataSetSecurityID] = useState(-1);
    const [dsDataSetSecurityEditorMode, setDSDataSetSecurityEditorMode] = useState<ManagedAdminActionMode>('idle');
    const [dsSelectedDataSetColumnFormatID, setDSSelectedDataSetColumnFormatID] = useState(-1);
    const [dsDataSetColumnFormatEditorMode, setDSDataSetColumnFormatEditorMode] = useState<ManagedAdminActionMode>('idle');
    const [dsSelectedDrilldownID, setDSSelectedDrilldownID] = useState(-1);
    const [dsDrilldownEditorMode, setDSDrilldownEditorMode] = useState<ManagedAdminActionMode>('idle');

    const [dsIncludeDisabled] = useState(false);

    const [dsSelectedDataSetCreatedBy, setDSSelectedDataSetCreatedBy] = useState(0);
    const [dsSelectedDataSetCreatedTs, setDSSelectedDataSetCreatedTs] = useState(new Date());
    const [dsSelectedDataSetDeletedBy, setDSSelectedDataSetDeletedBy] = useState<number | null>(null);
    const [dsSelectedDataSetDeletedTs, setDSSelectedDataSetDeletedTs] = useState<Date | null>(null);

    const showDSInfoModal = (title: string, message: string, isError: boolean = true) => {
        setManageActionModalState(buildManagedActionModalState('info', title, message, isError));
    };

    const buildDSErrorMessage = (prefix: string, err: unknown): string => {
        const apiError = err as ManagedApiError;

        return apiError?.response?.data?.message
            ? `${prefix}: ${apiError.response.data.message}`
            : `${prefix}: ${String(err)}`;
    };

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredDSField = (fieldName: string): Promise<void> => {
        const value = formAUDDataSets.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateDSDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'dsDetailsName':
            case 'dsDetailsQuery':
                return validateRequiredDSField(rule.field);

            case 'dsDetailsGroupBy':
            case 'dsDetailsOrderBy':
            case 'dsDetailsAllowFiltering':
            case 'dsDetailsGroupingGeneric':
            case 'dsDetailsNameGeneric':
            case 'dsDetailsGrouping':
            case 'dsDetailsHideFromPortal':
            case 'dsDetailsAlwaysShowFilters':
            case 'dsDetailsInvolvesEncryption':
            case 'dsDetailsIsolationLevel':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const setDSMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const getDSOperation = (): ManagedAdminOperation  => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Upd';
        if (delButtonClicked) return 'Del';
        return '';
    };

    const getDSActionModalMode = (): ManagedActionModalMode => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        if (delButtonClicked) return 'delete';
        return 'info';
    };

    const getDSActionLabel = (): string => {
        if (addButtonClicked) return 'Add';
        if (updButtonClicked) return 'Update';
        if (delButtonClicked) return 'Delete';
        return 'Manage';
    };

    const getDSActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const getDSActionTargetName = (): string => {
        return dsSelectedDataSetName || formAUDDataSets.getFieldValue('dsDetailsName') || '';
    };

    const showDSResultModal = (isError: boolean, message: string) => {
        setManageActionModalState(buildManagedActionModalState(getDSActionModalMode(), `${getDSActionLabel()} DataSet`, message, isError));
    };

    const clearDSSelection = () => {
        setDSSelectedDataSetID(-1);
        setDSSelectedDataSetName('');
        setDSSelectedDataSetCreatedBy(0);
        setDSSelectedDataSetCreatedTs(new Date());
        setDSSelectedDataSetDeletedBy(null);
        setDSSelectedDataSetDeletedTs(null);
        setDSMode('add');
        setManageDataSetSecuritiesModalOpen(false);
        setManageDataSetInputsModalOpen(false);
        setManageDataSetColumnFormatsModalOpen(false);
        setManageDataSetDrilldownsModalOpen(false);
        setDSBlankForm();
    };

    const buildAUDDataSetRequest = (): AUDDataSetRequest => {
        const dsDetailsName = formAUDDataSets.getFieldValue('dsDetailsName');
        const dsDetailsQuery = formAUDDataSets.getFieldValue('dsDetailsQuery');
        const dsDetailsGroupBy = formAUDDataSets.getFieldValue('dsDetailsGroupBy');
        const dsDetailsOrderBy = formAUDDataSets.getFieldValue('dsDetailsOrderBy');
        const dsDetailsGrouping = formAUDDataSets.getFieldValue('dsDetailsGrouping');
        const dsDetailsAllowFiltering = formAUDDataSets.getFieldValue('dsDetailsAllowFiltering');
        const dsDetailsGroupingGeneric = formAUDDataSets.getFieldValue('dsDetailsGroupingGeneric');
        const dsDetailsNameGeneric = formAUDDataSets.getFieldValue('dsDetailsNameGeneric');
        const dsDetailsHideFromPortal = formAUDDataSets.getFieldValue('dsDetailsHideFromPortal');
        const dsDetailsAlwaysShowFilters = formAUDDataSets.getFieldValue('dsDetailsAlwaysShowFilters');
        const dsDetailsInvolvesEncryption = formAUDDataSets.getFieldValue('dsDetailsInvolvesEncryption');
        const dsDetailsIsolationLevel = formAUDDataSets.getFieldValue('dsDetailsIsolationLevel')
            ? 'READ UNCOMMITTED'
            : '';

        return {
            id: dsSelectedDataSetID,
            name: dsDetailsName ?? '',
            query: dsDetailsQuery ?? '',
            groupBy: dsDetailsGroupBy ?? '',
            orderBy: dsDetailsOrderBy ?? '',
            grouping: dsDetailsGrouping ?? '',
            allowFiltering: dsDetailsAllowFiltering ?? false,
            created_By: addButtonClicked ? (user?.id ?? 0) : dsSelectedDataSetCreatedBy,
            modified_By: user?.id ?? 0,
            deleted_By: delButtonClicked ? (user?.id ?? 0) : dsSelectedDataSetDeletedBy,
            created_Ts: addButtonClicked ? new Date() : dsSelectedDataSetCreatedTs,
            modified_Ts: new Date(),
            deleted_Ts: delButtonClicked ? new Date() : dsSelectedDataSetDeletedTs,
            groupingGeneric: dsDetailsGroupingGeneric ?? '',
            nameGeneric: dsDetailsNameGeneric ?? '',
            hideFromPortal: dsDetailsHideFromPortal ?? false,
            always_Show_Filters: dsDetailsAlwaysShowFilters ?? false,
            involves_Encryption: dsDetailsInvolvesEncryption ?? false,
            isolation_Level: dsDetailsIsolationLevel ?? '',

            audDataSetSecurities: dsDetailsDataSetSecurities.map((eachDataSetSecurityElement) => {
                return {
                    id: eachDataSetSecurityElement.id,
                    dataSet_id: eachDataSetSecurityElement.dataSetId,
                    user_id: eachDataSetSecurityElement.userId,
                    tenant_id: eachDataSetSecurityElement.tenantId,
                    userRole_id: eachDataSetSecurityElement.userRoleId,
                    additionalWhere: eachDataSetSecurityElement.additionalWhere,
                    created_By: eachDataSetSecurityElement.createdBy,
                    modified_By: eachDataSetSecurityElement.modifiedBy,
                    deleted_By: eachDataSetSecurityElement.deletedBy,
                    created_Ts: eachDataSetSecurityElement.createdTs,
                    modified_Ts: eachDataSetSecurityElement.modifiedTs,
                    deleted_Ts: eachDataSetSecurityElement.deletedTs,
                };
            }),

            audDataSetInputs: dsDetailsDataSetInputs.map((eachDataSetInputElement) => {
                return {
                    id: eachDataSetInputElement.id,
                    dataSet_id: eachDataSetInputElement.dataSetId,
                    title: eachDataSetInputElement.title,
                    inputName: eachDataSetInputElement.inputName,
                    sourceQuery: eachDataSetInputElement.sourceQuery,
                    multiSelect: eachDataSetInputElement.multiSelect,
                    selector: eachDataSetInputElement.selector,
                    dataFormat: eachDataSetInputElement.dataFormat,
                    required: eachDataSetInputElement.required,
                    whereQuery: eachDataSetInputElement.whereQuery,
                    created_By: eachDataSetInputElement.createdBy,
                    modified_By: eachDataSetInputElement.modifiedBy,
                    deleted_By: eachDataSetInputElement.deletedBy,
                    created_Ts: eachDataSetInputElement.createdTs,
                    modified_Ts: eachDataSetInputElement.modifiedTs,
                    deleted_Ts: eachDataSetInputElement.deletedTs,
                    global_designator: eachDataSetInputElement.globalDesignator,
                    sort: eachDataSetInputElement.sort,
                    parent_id: eachDataSetInputElement.parentId,
                    defaultValue: eachDataSetInputElement.defaultValue,
                };
            }),

            audDataSetColumnFormats: dsDetailsDataSetColumnFormats.map((eachDataSetColumnFormatElement) => {
                return {
                    id: eachDataSetColumnFormatElement.id,
                    dataSet_id: eachDataSetColumnFormatElement.dataSetId,
                    columnName: eachDataSetColumnFormatElement.columnName,
                    format: eachDataSetColumnFormatElement.format,
                    aggregate: eachDataSetColumnFormatElement.aggregate,
                    allowFiltering: eachDataSetColumnFormatElement.allowFiltering,
                    groupColumn: eachDataSetColumnFormatElement.groupColumn,
                    includeInGrandTotal: eachDataSetColumnFormatElement.includeInGrandTotal,
                    visible: eachDataSetColumnFormatElement.visible,
                    created_By: eachDataSetColumnFormatElement.createdBy,
                    modified_By: eachDataSetColumnFormatElement.modifiedBy,
                    deleted_By: eachDataSetColumnFormatElement.deletedBy,
                    created_Ts: eachDataSetColumnFormatElement.createdTs,
                    modified_Ts: eachDataSetColumnFormatElement.modifiedTs,
                    deleted_Ts: eachDataSetColumnFormatElement.deletedTs,
                    alignment: eachDataSetColumnFormatElement.alignment,
                    color_BG: eachDataSetColumnFormatElement.colorBG,
                    color_Fore: eachDataSetColumnFormatElement.colorFore,
                    format_Excel: eachDataSetColumnFormatElement.formatExcel,
                    columnTitle: eachDataSetColumnFormatElement.columnTitle,
                    toolTip: eachDataSetColumnFormatElement.toolTip,
                    user_id: eachDataSetColumnFormatElement.userId,
                    width_Excel: eachDataSetColumnFormatElement.widthExcel,
                    allowHTML: eachDataSetColumnFormatElement.allowHTML,
                    jsDataType: eachDataSetColumnFormatElement.jsDataType,
                };
            }),

            audDrilldowns: dsDetailsDrilldowns.map((eachDrilldown) => {
                return {
                    id: eachDrilldown.id,
                    source_DataSet_ID: eachDrilldown.sourceDataSetId,
                    source_Chart_DataSet_ID: eachDrilldown.sourceChartDataSetId,
                    source_Value: eachDrilldown.sourceValue,
                    target_DataSet_ID: eachDrilldown.targetDataSetId,
                    target_Chart_ID: eachDrilldown.targetChartId,
                    target_DataSet_Input_ID: eachDrilldown.targetDataSetInputId,
                    audDrilldownInputPassthroughMaps: eachDrilldown.drilldownInputPassthroughMaps.map((eachPassthroughMap) => {
                        return {
                            id: eachPassthroughMap.id,
                            drilldown_ID: eachPassthroughMap.drilldownId,
                            source_DataSet_Input_ID: eachPassthroughMap.sourceDataSetInputId,
                            target_DataSet_Input_ID: eachPassthroughMap.targetDataSetInputId,
                            operation: eachPassthroughMap.id > 0 ? 'Upd' : 'Add',
                        };
                    }),
                    operation: eachDrilldown.id > 0 ? 'Upd' : 'Add',
                };
            }),

            syncDrilldowns: true,

            operation: getDSOperation(),
        };
    };

    const { data: aiProviders = [], isLoading: isGetAIProvidersLoading } = useQuery<GetAIProviderListModelResponse[], unknown>(
        ['aud-dataset-ai-providers'],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpGet_GetAIProviders`;
                const requestData: GetAIProviderListModelRequest = {};

                return await AxiosUtil.get<GetAIProviderListModelResponse[], GetAIProviderListModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_DATASET_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                console.error('AUD_DATASET_AI_PROVIDERS: [Get AI Providers] Internal Error: ', err);
                setManageActionModalState(
                    buildManagedActionModalState(
                        'info',
                        'AI Provider Load Error',
                        'Unable to load AI Providers for DataSet Query assistance.',
                        true
                    )
                );
            },
        }
    );

    const getAIContextAssistProvider = (): GetAIProviderListModelResponse | undefined => {
        const providerNamePhrase = aiContextAssistContext.providerNamePhrase.toLowerCase();

        return aiProviders.find((provider) =>
            provider.name.toLowerCase().includes(providerNamePhrase) ||
            provider.displayName.toLowerCase().includes(providerNamePhrase)
        );
    };

    const buildAIContextAssistPrompt = (requestText: string): string => {
        const currentSQLText = aiContextAssistContext.allowUseSuggestion ? formAUDDataSets.getFieldValue('dsDetailsQuery') ?? '' : aiContextAssistCurrentText;
        const outputInstructions = aiContextAssistContext.allowUseSuggestion
            ? [
                `Return only the clean, drop-in ready ${aiContextAssistContext.targetTextDescription}.`,
                'Do not include markdown, explanation, labels, or surrounding commentary in the suggested SQL.',
                'Do not suggest executing SQL. The user must review and apply any suggestion manually.',
            ]
            : [
                'Explain the current DataSet query in clear BI/reporting terms.',
                'Focus on what the query returns, important joins, filters, grouping, ordering, and any assumptions or risks that are visible from the SQL.',
                'Do not rewrite the SQL unless the user specifically asks for a rewrite.',
            ];

        return [
            'RetainBI SQL assistance request.',
            `Assist area: ${aiContextAssistContext.title}`,
            '',
            'Current SQL/context:',
            currentSQLText,
            '',
            'User request:',
            requestText,
            '',
            'Output instructions:',
            ...outputInstructions,
            'Do not include credentials, connection strings, secrets, PHI, PII, tenant-isolated data, or system security details.',
        ].join('\n');
    };

    const { mutate: getAIContextAssistPrompt } = useMutation(
        async ({ externalVendorID, tenantID, prompt }: GetAISingleTurnPromptModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AISingleTurnPrompt`;
                const requestData: GetAISingleTurnPromptModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    prompt: prompt,
                };

                return await AxiosUtil.post<GetAISingleTurnPromptModelResponse, GetAISingleTurnPromptModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_DATASET_AI_CONTEXT_ASSIST: [AI Context Assist] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAISingleTurnPromptModelResponse) => {
                const rawResponseText = response.text ?? '';
                const resultText = aiContextAssistContext.allowUseSuggestion
                    ? extractAISQLAssistSuggestionText(rawResponseText)
                    : rawResponseText.trim();
                const responseWasCleaned = rawResponseText.trim() !== resultText.trim();

                setAIContextAssistResultText(resultText);
                setAIContextAssistAncillaryItems([
                    response.success ? 'AI response returned successfully.' : 'AI response request did not succeed.',
                    responseWasCleaned ? 'RetainBI removed explanatory text before showing the suggested SQL.' : '',
                    response.message ?? '',
                ].filter((item) => item.trim().length > 0));
                setAIContextAssistLoading(false);
            },
            onError: (err: unknown) => {
                console.error('AUD_DATASET_AI_CONTEXT_ASSIST: [AI Context Assist] Internal Error: ', err);
                setAIContextAssistAncillaryItems([
                    'AI suggestion request failed. Please contact application support if the issue continues.',
                ]);
                setAIContextAssistLoading(false);
            },
        }
    );

    const { mutate: getAIDataSetExplain } = useMutation(
        async ({ externalVendorID, tenantID, dataSetID, prompt }: GetAIDataSetExplainModelRequest) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}/api/AI/httpPost_AIDataSetExplain`;
                const requestData: GetAIDataSetExplainModelRequest = {
                    externalVendorID: externalVendorID,
                    tenantID: tenantID,
                    dataSetID: dataSetID,
                    prompt: prompt,
                };

                return await AxiosUtil.post<GetAIExplainModelResponse, GetAIDataSetExplainModelRequest>(url, requestData);
            } catch (err) {
                console.error('AUD_DATASET_AI_EXPLAIN: [AI DataSet Explain] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onSuccess: (response: GetAIExplainModelResponse) => {
                setAIContextAssistResultText((response.text ?? '').trim());
                setAIContextAssistAncillaryItems([
                    response.success ? 'AI DataSet Explain returned successfully.' : 'AI DataSet Explain request did not succeed.',
                    response.message ?? '',
                ].filter((item) => item.trim().length > 0));
                setAIContextAssistLoading(false);
            },
            onError: (err: unknown) => {
                console.error('AUD_DATASET_AI_EXPLAIN: [AI DataSet Explain] Internal Error: ', err);
                setAIContextAssistAncillaryItems([
                    'AI DataSet Explain request failed. Please contact application support if the issue continues.',
                ]);
                setAIContextAssistLoading(false);
            },
        }
    );

    const { data: dataSets = [], isLoading: isGetDataSetsLoading, refetch: refetchDataSets } = useQuery<GetDataSetAdminModelsWithChildrenResponse[], Error, ManagedDataSet[]>(
        ['dataSetAdminModelsWithChildren', dsIncludeDisabled],
        async (): Promise<GetDataSetAdminModelsWithChildrenResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_DATASET_ADMIN_MODELS_WITH_CHILDREN}`;
                const requestData: GetDataSetAdminModelsWithChildrenRequest = { gdsac_includeDisabled: dsIncludeDisabled };
                return await AxiosUtil.get<GetDataSetAdminModelsWithChildrenResponse[], GetDataSetAdminModelsWithChildrenRequest>(url, requestData);
            } catch (err) {
                console.error('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models AUD t/c] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                console.error('GET_DATASET_ADMIN_MODELS: [Get DataSet Admin Models AUD] Internal Error: ', err);
                showDSInfoModal('DataSet Load Error', buildDSErrorMessage('Unable to load DataSets for Manage DataSets.', err));
            },
            select: (response: GetDataSetAdminModelsWithChildrenResponse[]): ManagedDataSet[] =>
                response
                    .map(mapDataSetAdminModel)
                    .sort((a, b) => a.name.localeCompare(b.name)),
        }
    );

    const { mutate: audDataSet, isLoading: isAUDDataSetLoading, reset: resetAUDDataSet } = useMutation<boolean, Error, void>(
        async (): Promise<boolean> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.AUD_DATASET_DETAILS_BY_ID}`;
                const requestData: AUDDataSetRequest = buildAUDDataSetRequest();
                const responseData = await AxiosUtil.post<boolean, AUDDataSetRequest>(url, requestData);
                return responseData;
            } catch (err) {
                console.error('AUD_DATASET_DETAILS_BY_ID: [AUD DataSet t/c] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                const dataSetName = getDSActionTargetName();

                console.error('AUD_DATASET_DETAILS_BY_ID: [AUD DataSet o/e] Internal Error: ', err);

                showDSResultModal(true, `${getDSActionLabel()} DataSet: ${dataSetName} Failed. Please Contact Application Support`);
            },
            onSuccess: async (response: boolean) => {
                const dataSetName = getDSActionTargetName();
                const actionLabel = getDSActionLabel();

                if (delButtonClicked) {
                    clearDSSelection();
                    await refetchDataSets();
                    showDSResultModal(false, `${actionLabel} DataSet: ${dataSetName} Succeeded.`);
                    return;
                }

                const refetchResult = await refetchDataSets();
                const refreshedDataSet = refetchResult.data?.find((dataSet) => dataSet.id === dsSelectedDataSetID);

                if (refreshedDataSet) {
                    setDSFormFromSelectedDataSet(refreshedDataSet);
                }

                showDSResultModal(false, `${actionLabel} DataSet: ${dataSetName} Succeeded.`);
            },
        }
    );

    const getDataSetSecuritiesEditorContext = () => {
        const selectedDataSet = getSelectedDataSet();

        return {
            dataSetID: dsSelectedDataSetID > 0 ? dsSelectedDataSetID : 0,
            dataSetName: formAUDDataSets.getFieldValue('dsDetailsName') ?? selectedDataSet?.name ?? '',
            dataSetSecurities: dsDetailsDataSetSecurities,
            initialSelectedDataSetSecurityID: dsSelectedDataSetSecurityID,
            initialMode: dsDataSetSecurityEditorMode,
        };
    };

    const openDSDataSetSecuritiesEditor = (
        mode: ManagedAdminChildOperation,
        selectedDataSetSecurityID: number = -1
    ) => {
        setDSDataSetSecurityEditorMode(mode);
        setDSSelectedDataSetSecurityID(selectedDataSetSecurityID);
        setManageDataSetSecuritiesModalOpen(true);
    };

    const closeDSDataSetSecuritiesEditor = () => {
        setManageDataSetSecuritiesModalOpen(false);
        setDSDataSetSecurityEditorMode('idle');
        setDSSelectedDataSetSecurityID(-1);
    };

    const applyDataSetSecurityEditResult = (
        existingDataSetSecurities: ManagedDataSetSecurity[],
        result: ManagedDataSetSecurityEditResult
    ): ManagedDataSetSecurity[] => {
        const { operation, dataSetSecurity } = result;

        switch (operation) {
            case 'add':
                return [...existingDataSetSecurities, dataSetSecurity];

            case 'update':
                return existingDataSetSecurities.map((eachDataSetSecurity) =>
                    eachDataSetSecurity.id === dataSetSecurity.id ? dataSetSecurity : eachDataSetSecurity
                );

            case 'delete':
                return existingDataSetSecurities.filter(
                    (eachDataSetSecurity) => eachDataSetSecurity.id !== dataSetSecurity.id
                );

            default:
                return existingDataSetSecurities;
        }
    };

    const handleSetDataSetSecurity = (result: ManagedDataSetSecurityEditResult) => {
        setDSDetailsDataSetSecurities((currentDataSetSecurities) =>
            applyDataSetSecurityEditResult(currentDataSetSecurities, result)
        );
    };

    const onDSSecurity = () => {
        openDSDataSetSecuritiesEditor('add');
    }

    const getDataSetInputsEditorContext = () => {
        const selectedDataSet = getSelectedDataSet();

        return {
            dataSetID: dsSelectedDataSetID > 0 ? dsSelectedDataSetID : 0,
            dataSetName: formAUDDataSets.getFieldValue('dsDetailsName') ?? selectedDataSet?.name ?? '',
            dataSetInputs: dsDetailsDataSetInputs,
            initialSelectedDataSetInputID: dsSelectedDataSetInputID,
            initialMode: dsDataSetInputEditorMode,
        };
    };

    const openDSDataSetInputsEditor = (
        mode: ManagedAdminChildOperation,
        selectedDataSetInputID: number = -1
    ) => {
        setDSDataSetInputEditorMode(mode);
        setDSSelectedDataSetInputID(selectedDataSetInputID);
        setManageDataSetInputsModalOpen(true);
    };

    const closeDSDataSetInputsEditor = () => {
        setManageDataSetInputsModalOpen(false);
        setDSDataSetInputEditorMode('idle');
        setDSSelectedDataSetInputID(-1);
    };

    const applyDataSetInputEditResult = (
        existingDataSetInputs: ManagedDataSetInput[],
        result: ManagedDataSetInputEditResult
    ): ManagedDataSetInput[] => {
        const { operation, dataSetInput } = result;

        switch (operation) {
            case 'add':
                return [...existingDataSetInputs, dataSetInput];

            case 'update':
                return existingDataSetInputs.map((eachDataSetInput) =>
                    eachDataSetInput.id === dataSetInput.id ? dataSetInput : eachDataSetInput
                );

            case 'delete':
                return existingDataSetInputs.filter(
                    (eachDataSetInput) => eachDataSetInput.id !== dataSetInput.id
                );

            default:
                return existingDataSetInputs;
        }
    };

    const handleSetDataSetInput = (result: ManagedDataSetInputEditResult) => {
        setDSDetailsDataSetInputs((currentDataSetInputs) =>
            applyDataSetInputEditResult(currentDataSetInputs, result)
        );
    };

    const onDSInput = () => {
        openDSDataSetInputsEditor('add');
    }

    const getDataSetColumnFormatsEditorContext = () => {
        const selectedDataSet = getSelectedDataSet();

        return {
            dataSetID: dsSelectedDataSetID > 0 ? dsSelectedDataSetID : 0,
            dataSetName: formAUDDataSets.getFieldValue('dsDetailsName') ?? selectedDataSet?.name ?? '',
            dataSetColumnFormats: dsDetailsDataSetColumnFormats,
            initialSelectedDataSetColumnFormatID: dsSelectedDataSetColumnFormatID,
            initialMode: dsDataSetColumnFormatEditorMode,
        };
    };

    const openDSDataSetColumnFormatsEditor = (
        mode: ManagedAdminChildOperation,
        selectedDataSetColumnFormatID: number = -1
    ) => {
        setDSDataSetColumnFormatEditorMode(mode);
        setDSSelectedDataSetColumnFormatID(selectedDataSetColumnFormatID);
        setManageDataSetColumnFormatsModalOpen(true);
    };

    const closeDSDataSetColumnFormatsEditor = () => {
        setManageDataSetColumnFormatsModalOpen(false);
        setDSDataSetColumnFormatEditorMode('idle');
        setDSSelectedDataSetColumnFormatID(-1);
    };

    const applyDataSetColumnFormatEditResult = (
        existingDataSetColumnFormats: ManagedDataSetColumnFormat[],
        result: ManagedDataSetColumnFormatEditResult
    ): ManagedDataSetColumnFormat[] => {
        const { operation, dataSetColumnFormat } = result;

        switch (operation) {
            case 'add':
                return [...existingDataSetColumnFormats, dataSetColumnFormat];

            case 'update':
                return existingDataSetColumnFormats.map((eachDataSetColumnFormat) =>
                    eachDataSetColumnFormat.id === dataSetColumnFormat.id ? dataSetColumnFormat : eachDataSetColumnFormat
                );

            case 'delete':
                return existingDataSetColumnFormats.filter(
                    (eachDataSetColumnFormat) => eachDataSetColumnFormat.id !== dataSetColumnFormat.id
                );

            default:
                return existingDataSetColumnFormats;
        }
    };

    const handleSetDataSetColumnFormat = (result: ManagedDataSetColumnFormatEditResult) => {
        setDSDetailsDataSetColumnFormats((currentDataSetColumnFormats) =>
            applyDataSetColumnFormatEditResult(currentDataSetColumnFormats, result)
        );
    };

    const onDSColumnFormat = () => {
        openDSDataSetColumnFormatsEditor('add');
    };

        const getDataSetDrilldownsEditorContext = () => {
        const selectedDataSet = getSelectedDataSet();

        return {
            dataSetID: dsSelectedDataSetID > 0 ? dsSelectedDataSetID : 0,
            dataSetName: formAUDDataSets.getFieldValue('dsDetailsName') ?? selectedDataSet?.name ?? '',
            drilldowns: dsDetailsDrilldowns,
            initialSelectedDrilldownID: dsSelectedDrilldownID,
            initialMode: dsDrilldownEditorMode,
        };
    };

    const openDSDataSetDrilldownsEditor = (
        mode: ManagedAdminChildOperation,
        selectedDrilldownID: number = -1
    ) => {
        setDSDrilldownEditorMode(mode);
        setDSSelectedDrilldownID(selectedDrilldownID);
        setManageDataSetDrilldownsModalOpen(true);
    };

    const closeDSDataSetDrilldownsEditor = () => {
        setManageDataSetDrilldownsModalOpen(false);
        setDSDrilldownEditorMode('idle');
        setDSSelectedDrilldownID(-1);
    };

    const applyDrilldownEditResult = (
        existingDrilldowns: ManagedDrilldown[],
        result: ManagedDrilldownEditResult
    ): ManagedDrilldown[] => {
        const { operation, drilldown } = result;

        switch (operation) {
            case 'add':
                return [...existingDrilldowns, drilldown];

            case 'update':
                return existingDrilldowns.map((eachDrilldown) =>
                    eachDrilldown.id === drilldown.id ? drilldown : eachDrilldown
                );

            case 'delete':
                return existingDrilldowns.filter(
                    (eachDrilldown) => eachDrilldown.id !== drilldown.id
                );

            default:
                return existingDrilldowns;
        }
    };

    const handleSetDrilldowns = (result: ManagedDrilldownEditResult) => {
        setDSDetailsDrilldowns((currentDrilldowns) =>
            applyDrilldownEditResult(currentDrilldowns, result)
        );
    };

    const onDSDrilldowns = () => {
        openDSDataSetDrilldownsEditor('add');
    };

    const onFinishFailed = (_errorInfo: ManagedFormErrorInfo<AUDDataSetFormValues>) => {
        showDSInfoModal('DataSet Validation Error', 'Please correct the highlighted DataSet fields and try again.');
    };

    const onFinish = () => {
        audDataSet();
    };

    const getSelectedDataSet = (selectedDataSetID: number = dsSelectedDataSetID): ManagedDataSet | undefined => {
        return dataSets.find((dataSet) => dataSet.id === selectedDataSetID);
    };

    const handleDSSelectionChange = (value: number) => {
        setDSSelectedDataSetID(value);

        const selectedDataSet = getSelectedDataSet(value);

        setDSSelectedDataSetName(selectedDataSet?.name ?? '');
        setDSSelectedDataSetCreatedBy(selectedDataSet?.createdBy ?? 0);
        setDSSelectedDataSetCreatedTs(selectedDataSet?.createdTs ?? new Date());
        setDSSelectedDataSetDeletedBy(selectedDataSet?.deletedBy ?? null);
        setDSSelectedDataSetDeletedTs(selectedDataSet?.deletedTs ?? null);

        setDSMode('add');
        setDSBlankForm();
    };

    const setDSBlankForm = () => {
        formAUDDataSets.setFieldValue('dsDetailsName', '');
        formAUDDataSets.setFieldValue('dsDetailsQuery', '');
        formAUDDataSets.setFieldValue('dsDetailsGroupBy', '');
        formAUDDataSets.setFieldValue('dsDetailsOrderBy', '');
        formAUDDataSets.setFieldValue('dsDetailsGrouping', '');
        formAUDDataSets.setFieldValue('dsDetailsAllowFiltering', false);
        formAUDDataSets.setFieldValue('dsDetailsGroupingGeneric', '');
        formAUDDataSets.setFieldValue('dsDetailsNameGeneric', '');
        formAUDDataSets.setFieldValue('dsDetailsHideFromPortal', false);
        formAUDDataSets.setFieldValue('dsDetailsAlwaysShowFilters', false);
        formAUDDataSets.setFieldValue('dsDetailsInvolvesEncryption', false);
        formAUDDataSets.setFieldValue('dsDetailsIsolationLevel', true);

        setDSDetailsDataSetSecurities([]);
        setDSDetailsDataSetInputs([]);
        setDSDetailsDataSetColumnFormats([]);
        setDSDetailsDrilldowns([]);
    };

    const setDSFormFromSelectedDataSet = (dataSet: ManagedDataSet | undefined = getSelectedDataSet()) => {
        const selectedDataSet = dataSet;

        formAUDDataSets.setFieldValue('dsDetailsName', selectedDataSet?.name ?? '');
        formAUDDataSets.setFieldValue('dsDetailsQuery', selectedDataSet?.query ?? '');
        formAUDDataSets.setFieldValue('dsDetailsGroupBy', selectedDataSet?.groupBy ?? '');
        formAUDDataSets.setFieldValue('dsDetailsOrderBy', selectedDataSet?.orderBy ?? '');
        formAUDDataSets.setFieldValue('dsDetailsGrouping', selectedDataSet?.grouping ?? '');
        formAUDDataSets.setFieldValue('dsDetailsAllowFiltering', selectedDataSet?.allowFiltering ?? false);
        formAUDDataSets.setFieldValue('dsDetailsGroupingGeneric', selectedDataSet?.groupingGeneric ?? '');
        formAUDDataSets.setFieldValue('dsDetailsNameGeneric', selectedDataSet?.nameGeneric ?? '');
        formAUDDataSets.setFieldValue('dsDetailsHideFromPortal', selectedDataSet?.hideFromPortal ?? false);
        formAUDDataSets.setFieldValue('dsDetailsAlwaysShowFilters', selectedDataSet?.alwaysShowFilters ?? false);
        formAUDDataSets.setFieldValue('dsDetailsInvolvesEncryption', selectedDataSet?.involvesEncryption ?? false);
        formAUDDataSets.setFieldValue(
            'dsDetailsIsolationLevel',
            (selectedDataSet?.isolationLevel ?? 'READ UNCOMMITTED') === 'READ UNCOMMITTED'
        );

        setDSDetailsDataSetSecurities(selectedDataSet?.dataSetSecurities ?? []);
        setDSDetailsDataSetInputs(selectedDataSet?.dataSetInputs ?? []);
        setDSDetailsDataSetColumnFormats(selectedDataSet?.dataSetColumnFormats ?? []);
        setDSDetailsDrilldowns(selectedDataSet?.drilldowns ?? []);
    };

    const handleDSDataSetResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setDSBlankForm();
            return;
        }

        setDSFormFromSelectedDataSet();
    };

    const handleOpenAIContextAssistModal = () => {
        setAIContextAssistContext({
            title: 'AI DataSet Query Assist',
            currentSectionTitle: 'Current DataSet Query',
            requestSectionTitle: 'What do you want this DataSet query to do?',
            resultSectionTitle: 'Suggested DataSet Query',
            requestPlaceholder: 'Describe the DataSet query you want AI to help build or review.',
            askButtonText: 'Ask AI',
            useButtonText: 'Use Suggestion',
            providerNamePhrase: 'SQL Review',
            targetTextDescription: 'DataSet Query SQL text',
            allowUseSuggestion: true,
            assistMode: 'sqlAssist',
        });
        setAIContextAssistCurrentText(formAUDDataSets.getFieldValue('dsDetailsQuery') ?? '');
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);
        setAIContextAssistModalOpen(true);
    };

    const buildDataSetExplainSelectionSummary = (dataSet: ManagedDataSet | undefined): string => {
        if (!dataSet) {
            return 'No DataSet selected.';
        }

        return [
            `DataSet ID: ${dataSet.id}`,
            `DataSet Name: ${dataSet.name}`,
            `Generic Name: ${dataSet.nameGeneric ?? ''}`,
            `Grouping: ${dataSet.grouping ?? ''}`,
            `Input Count: ${dataSet.dataSetInputs.length}`,
            `Column Format Count: ${dataSet.dataSetColumnFormats.length}`,
            `Drilldown Count: ${dataSet.drilldowns.length}`,
            '',
            'The detailed DataSet Explain context is built by the backend.',
        ].join('\n');
    };

    const handleOpenAIDataSetExplainModal = () => {
        const selectedDataSet = getSelectedDataSet();

        setAIContextAssistContext({
            title: 'AI DataSet Explain',
            currentSectionTitle: 'Current DataSet Selection',
            requestSectionTitle: 'What do you want explained?',
            resultSectionTitle: 'DataSet Explanation',
            requestPlaceholder: 'Optionally describe what part of this DataSet you want explained.',
            askButtonText: 'Explain DataSet',
            useButtonText: 'Use Suggestion',
            providerNamePhrase: 'DataSet Explain',
            targetTextDescription: 'plain-language DataSet explanation',
            allowUseSuggestion: false,
            assistMode: 'dataSetExplain',
        });

        setAIContextAssistCurrentText(buildDataSetExplainSelectionSummary(selectedDataSet));
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);
        setAIContextAssistModalOpen(true);
    };

    const handleAskAIContextAssist = (requestText: string) => {
        const aiContextAssistProvider = getAIContextAssistProvider();

        if (!aiContextAssistProvider) {
            setAIContextAssistAncillaryItems([
                `Unable to find an AI Provider matching ${aiContextAssistContext.providerNamePhrase}.`,
            ]);
            return;
        }

        setAIContextAssistLoading(true);
        setAIContextAssistResultText('');
        setAIContextAssistAncillaryItems([]);

        if (aiContextAssistContext.assistMode === 'dataSetExplain') {
            getAIDataSetExplain({
                externalVendorID: aiContextAssistProvider.externalVendorID,
                tenantID: null,
                dataSetID: dsSelectedDataSetID,
                prompt: requestText,
            });
            return;
        }

        getAIContextAssistPrompt({
            externalVendorID: aiContextAssistProvider.externalVendorID,
            tenantID: null,
            prompt: buildAIContextAssistPrompt(requestText),
        });
    };

    const handleCopyAIContextAssistResult = (resultText: string) => {
        navigator.clipboard.writeText(resultText);
    };

    const handleUseAIContextAssistSuggestion = (resultText: string) => {
        formAUDDataSets.setFieldValue('dsDetailsQuery', resultText);
        setAIContextAssistModalOpen(false);
    };

    //=================================================================
    //=================================================================
    
    const handleDSAddDataSet = () => {
        resetAUDDataSet();
        clearDSSelection();
    };

    //=================================================================
    //=================================================================

    const handleDSUpdDataSet = () => {
        resetAUDDataSet();
        setDSMode('update');
        handleDSDataSetResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleDSDelDataSet = () => {
        resetAUDDataSet();
        setDSMode('delete');
        handleDSDataSetResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleManageModalOk = () => {
        setManageActionModalState((prev) => ({
            ...prev,
            open: false,
        }));
    };

    const onDSCancel = () => {
        resetAUDDataSet();
        clearDSSelection();
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '8px',
        height: '470px'
    };
    
    return (
        <Card className='shadow-card admin-full-page-card' size='small'>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageModalOk}
            />
            <AIContextAssistModal
                open={aiContextAssistModalOpen}
                title={aiContextAssistContext.title}
                currentSectionTitle={aiContextAssistContext.currentSectionTitle}
                requestSectionTitle={aiContextAssistContext.requestSectionTitle}
                resultSectionTitle={aiContextAssistContext.resultSectionTitle}
                ancillarySectionTitle='Ancillary Information'
                currentText={aiContextAssistCurrentText}
                resultText={aiContextAssistResultText}
                ancillaryItems={aiContextAssistAncillaryItems}
                requestPlaceholder={aiContextAssistContext.requestPlaceholder}
                askButtonText={aiContextAssistContext.askButtonText}
                useButtonText={aiContextAssistContext.useButtonText}
                loading={aiContextAssistLoading || isGetAIProvidersLoading}
                allowUseSuggestion={aiContextAssistContext.allowUseSuggestion}
                onAskAI={handleAskAIContextAssist}
                onCopy={handleCopyAIContextAssistResult}
                onUseSuggestion={handleUseAIContextAssistSuggestion}
                onClose={() => setAIContextAssistModalOpen(false)}
            />
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='60%'
                open={manageDataSetSecuritiesModalOpen}
                onCancel={closeDSDataSetSecuritiesEditor}
            >
                {
                    manageDataSetSecuritiesModalOpen &&
                    (
                        <AUDDataSetSecurities
                            dataSetID={getDataSetSecuritiesEditorContext().dataSetID}
                            dataSetName={getDataSetSecuritiesEditorContext().dataSetName}
                            dataSetSecurities={getDataSetSecuritiesEditorContext().dataSetSecurities}
                            initialSelectedDataSetSecurityID={getDataSetSecuritiesEditorContext().initialSelectedDataSetSecurityID}
                            initialMode={getDataSetSecuritiesEditorContext().initialMode}
                            handleSetDataSetSecurity={handleSetDataSetSecurity}
                            close={closeDSDataSetSecuritiesEditor}
                        />
                    )
                }
            </ServaModal>
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='60%'
                open={manageDataSetInputsModalOpen}
                onCancel={closeDSDataSetInputsEditor}
            >
                {
                    manageDataSetInputsModalOpen &&
                    (
                        <AUDDataSetInputs
                            dataSetID={getDataSetInputsEditorContext().dataSetID}
                            dataSetName={getDataSetInputsEditorContext().dataSetName}
                            dataSetInputs={getDataSetInputsEditorContext().dataSetInputs}
                            initialSelectedDataSetInputID={getDataSetInputsEditorContext().initialSelectedDataSetInputID}
                            initialMode={getDataSetInputsEditorContext().initialMode}
                            handleSetDataSetInput={handleSetDataSetInput}
                            close={closeDSDataSetInputsEditor}
                        />
                    )
                }
            </ServaModal>
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='60%'
                open={manageDataSetColumnFormatsModalOpen}
                onCancel={closeDSDataSetColumnFormatsEditor}
            >
                {
                    manageDataSetColumnFormatsModalOpen &&
                    (
                        <AUDDataSetColumnFormats
                            dataSetID={getDataSetColumnFormatsEditorContext().dataSetID}
                            dataSetName={getDataSetColumnFormatsEditorContext().dataSetName}
                            dataSetColumnFormats={getDataSetColumnFormatsEditorContext().dataSetColumnFormats}
                            initialSelectedDataSetColumnFormatID={getDataSetColumnFormatsEditorContext().initialSelectedDataSetColumnFormatID}
                            initialMode={getDataSetColumnFormatsEditorContext().initialMode}
                            handleSetDataSetColumnFormat={handleSetDataSetColumnFormat}
                            close={closeDSDataSetColumnFormatsEditor}
                        />
                    )
                }
            </ServaModal>
            <ServaModal
                closeIcon={<CloseCircleOutlined></CloseCircleOutlined>}
                destroyOnHidden={true}
                hideButtons={true}
                width='75%'
                open={manageDataSetDrilldownsModalOpen}
                onCancel={closeDSDataSetDrilldownsEditor}
            >
                {
                    manageDataSetDrilldownsModalOpen &&
                    (
                        <AUDDrilldowns
                            sourceType='dataset'
                            sourceDataSetID={getDataSetDrilldownsEditorContext().dataSetID}
                            sourceTitle={getDataSetDrilldownsEditorContext().dataSetName}
                            sourceSubtitle=''
                            drilldowns={getDataSetDrilldownsEditorContext().drilldowns}
                            initialSelectedDrilldownID={getDataSetDrilldownsEditorContext().initialSelectedDrilldownID}
                            initialMode={getDataSetDrilldownsEditorContext().initialMode}
                            availableSourceColumns={[]}
                            handleSetDrilldowns={handleSetDrilldowns}
                            close={closeDSDataSetDrilldownsEditor}
                        />
                    )
                }
            </ServaModal>
            <Spin spinning={
                            isGetDataSetsLoading ||
                            isAUDDataSetLoading ||
                            isGetAIProvidersLoading
                           }>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage DataSets</Title>
                    </Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{ float: 'right', paddingTop: '8px' }}>Select DataSet to Update or Delete:</Text>
                    </Col>
                    <Col span={10}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a DataSet'}
                            showSearch
                            style={{paddingTop: '4px', width: '100%'}}
                            value={dsSelectedDataSetID === -1 ? undefined : dsSelectedDataSetID}
                            onChange={handleDSSelectionChange}
                        >
                            {dataSets.map(
                                (eachDataSet) => {
                                    return (
                                        <Option
                                            key={eachDataSet.id}
                                            label={eachDataSet.name}
                                            value={eachDataSet.id}
                                        >
                                            {eachDataSet.name}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                    <Col span={1}>
                        <Button
                            disabled={dsSelectedDataSetID === -1 || isGetAIProvidersLoading}
                            icon={<BulbOutlinedIlluminated />}
                            style={{minWidth: '32px', padding: '2px',}}
                            title='AI DataSet Explain'
                            type='default'
                            onClick={handleOpenAIDataSetExplainModal}
                        />
                    </Col>
                    <Col span={3} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={3}>
                        <Button
                            icon={<AddIcon />}
                            style={{width: '95%', marginBottom: '4px', float: 'left',}}
                            type='primary'
                            onClick={handleDSAddDataSet}
                        >
                            Add DataSet
                        </Button>
                    </Col>
                    <Col span={9} />
                    <Col span={3}>
                        <Button
                            disabled={dsSelectedDataSetID === -1}
                            icon={<EditIcon />}
                            style={{width: '95%', backgroundColor: 'darkgray', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleDSUpdDataSet}
                        >
                            Update DataSet
                        </Button>
                    </Col>
                    <Col span={3}>
                        <Button
                            disabled={dsSelectedDataSetID === -1}
                            icon={<DeleteIcon />}
                            style={{width: '95%', backgroundColor: 'red', float: 'right', marginBottom: '4px',}}
                            type='primary'
                            onClick={handleDSDelDataSet}
                        >
                            Delete DataSet
                        </Button>
                    </Col>
                    <Col span={4} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Form
                    form={formAUDDataSets}
                    initialValues={{
                        dsDetailsAllowFiltering: false,
                        dsDetailsHideFromPortal: false,
                        dsDetailsAlwaysShowFilters: false,
                        dsDetailsInvolvesEncryption: false,
                        dsDetailsIsolationLevel: true
                    }}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <Row gutter={16} className='retainbi-compact-row'>
                        <Col span={1} />
                        <Col span={22}>
                            <div style={contentStyle}>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={22}>
                                        <Divider orientation='left' orientationMargin={50} variant='dotted' style={{ borderColor: 'black', color: getDSActionColor() }}>
                                            {addButtonClicked ? 'Add DataSet' :
                                                (updButtonClicked ? 'Update DataSet: [' + dsSelectedDataSetID + '] ' + dsSelectedDataSetName :
                                                    (delButtonClicked ? 'Delete DataSet: [' + dsSelectedDataSetID + '] ' + dsSelectedDataSetName : ''))}
                                        </Divider>
                                    </Col>
                                    <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Button
                                            style={{ color: getDSActionColor(), fontStyle: 'italic' }}
                                            type='link'
                                            onClick={() => handleDSDataSetResetForm(addButtonClicked ? 'blank' : 'selected')}
                                        >
                                            Reset Form
                                        </Button>
                                    </Col>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='dsdetailsname'
                                            name='dsDetailsName'
                                            label='Name'
                                            labelCol={{ span: 8 }}
                                            rules={[
                                                {
                                                    message: 'Name required.',
                                                    required: true,
                                                    validator: validateDSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Name'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            key='dsdetailsnamegeneric'
                                            name='dsDetailsNameGeneric'
                                            label='Name Generic'
                                            labelCol={{ span: 8 }}
                                            rules={[
                                                {
                                                    validator: validateDSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Name Generic'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='dsdetailsallowfiltering'
                                            name='dsDetailsAllowFiltering'
                                            rules={[
                                                {
                                                    validator: validateDSDetailsData,
                                                },
                                            ]}
                                            valuePropName='checked'
                                            style={{marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                                style={{float: 'left'}}
                                            >
                                                Allow Filtering
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='dsdetailshidefromportal'
                                            name='dsDetailsHideFromPortal'
                                            rules={[
                                                {
                                                    validator: validateDSDetailsData,
                                                },
                                            ]}
                                            valuePropName='checked'
                                            style={{marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                                style={{float: 'left'}}
                                            >
                                                Hide From Portal
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='dsdetailsgroupby'
                                            name='dsDetailsGroupBy'
                                            label='Group By'
                                            labelCol={{ span: 8 }}
                                            rules={[
                                                {
                                                    validator: validateDSDetailsData,
                                                },
                                            ]}
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Group By'
                                                style={{ color: delButtonClicked ? 'gray' : 'black' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            key='dsdetailsorderby'
                                            name='dsDetailsOrderBy'
                                            label='Order By'
                                            labelCol={{ span: 8 }}
                                            rules={[
                                                {
                                                    validator: validateDSDetailsData,
                                                },
                                            ]}
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Order By'
                                                style={{ color: delButtonClicked ? 'gray' : 'black' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='dsdetailsalwaysshowfilters'
                                            name='dsDetailsAlwaysShowFilters'
                                            rules={[
                                                {
                                                    validator: validateDSDetailsData,
                                                },
                                            ]}
                                            valuePropName='checked'
                                            style={{marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                                style={{float: 'left'}}
                                            >
                                                Always Show Filters
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='dsdetailsinvolvesencryption'
                                            name='dsDetailsInvolvesEncryption'
                                            rules={[
                                                {
                                                    validator: validateDSDetailsData,
                                                },
                                            ]}
                                            valuePropName='checked'
                                            style={{ marginBottom: '0px', marginTop: '0px' }}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                                style={{float: 'left'}}
                                            >
                                                Involves Encryption
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={8}>
                                        <Form.Item
                                            key='dsdetailsgrouping'
                                            name='dsDetailsGrouping'
                                            label='Grouping'
                                            labelCol={{ span: 8, }}
                                            rules={[
                                                {
                                                    validator: validateDSDetailsData,
                                                },
                                            ]}
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Grouping'
                                                style={{ color: delButtonClicked ? 'gray' : 'black' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            key='dsdetailsgroupinggeneric'
                                            name='dsDetailsGroupingGeneric'
                                            label='Grouping Generic'
                                            labelCol={{ span: 8, }}
                                            rules={[
                                                {
                                                    validator: validateDSDetailsData,
                                                },
                                            ]}
                                            style={{ marginBottom: '4px' }}
                                        >
                                            <Input
                                                disabled={delButtonClicked}
                                                placeholder='Enter Grouping Generic'
                                                style={{color: delButtonClicked ? 'gray' : 'black'}}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={1} />
                                    <Col span={7}>
                                        <Form.Item
                                            key='dsdetailsisolationlevel'
                                            name='dsDetailsIsolationLevel'
                                            rules={[
                                                {
                                                    validator: validateDSDetailsData,
                                                },
                                            ]}
                                            valuePropName='checked'
                                            style={{marginBottom: '0px', marginTop: '0px'}}
                                        >
                                            <Checkbox
                                                disabled={delButtonClicked}
                                                style={{float: 'left'}}
                                            >
                                                Isolation Level (set: READ UNCOMMITTED)
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <Form.Item
                                            key='dsdetailsquerycontainer'
                                            label='Query'
                                            style={{marginBottom: '4px'}}
                                        >
                                            <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                                                <Form.Item
                                                    key='dsdetailsquery'
                                                    name='dsDetailsQuery'
                                                    rules={[
                                                        {
                                                            message: 'Query required.',
                                                            required: true,
                                                            validator: validateDSDetailsData,
                                                        },
                                                    ]}
                                                    style={{ flex: 1, marginBottom: '0px' }}
                                                >
                                                    <TextArea
                                                        disabled={delButtonClicked}
                                                        placeholder='Enter Query'
                                                        rows={3}
                                                        style={{
                                                            color: delButtonClicked ? 'gray' : 'black',
                                                            resize: 'none',
                                                        }}
                                                    />
                                                </Form.Item>
                                                <Button
                                                    disabled={delButtonClicked || isGetAIProvidersLoading}
                                                    icon={<BulbOutlinedIlluminated />}
                                                    style={{
                                                        minWidth: '32px',
                                                        padding: '2px',
                                                    }}
                                                    title='AI Assist'
                                                    type='default'
                                                    onClick={handleOpenAIContextAssistModal}
                                                />
                                            </div>
                                        </Form.Item>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row'>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <div>
                                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                                        </div>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '4px' }}>
                                    <Col span={4}>
                                        <Form.Item
                                            key='dsdetailssecurities'
                                            name='dsDetailsSecurities'
                                            style={{marginBottom: '4px'}}
                                        >
                                            <Button
                                                style={{ width: '90%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                                type='primary'
                                                onClick={onDSSecurity}
                                            >
                                                DataSet Securities
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='dsdetailsinputs'
                                            name='dsDetailsInputs'
                                            style={{marginBottom: '0px'}}
                                        >
                                            <Button
                                                style={{ width: '90%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                                type='primary'
                                                onClick={onDSInput}
                                            >
                                                DataSet Inputs
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='dsdetailscolumnformats'
                                            name='dsDetailsColumnFormats'
                                            style={{marginBottom: '0px'}}
                                        >
                                            <Button
                                                style={{ width: '90%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                                type='primary'
                                                onClick={onDSColumnFormat}
                                            >
                                                DataSet Column Formats
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                    <Col span={4}>
                                        <Form.Item
                                            key='dsdetailsdrilldowns'
                                            name='dsDetailsDrilldowns'
                                            style={{marginBottom: '0px'}}
                                        >
                                            <Button
                                                style={{ width: '90%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                                type='primary'
                                                onClick={onDSDrilldowns}
                                            >
                                                DataSet Drilldowns
                                            </Button>
                                        </Form.Item>
                                    </Col>
                                    <Col span={8} />
                                </Row>
                                <Row gutter={16} className='retainbi-compact-row' style={{ visibility: addButtonClicked ? 'hidden' : 'visible' }}>
                                    <Col span={1} />
                                    <Col span={22}>
                                        <div>
                                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }} />
                                            <Text italic style={{ color: 'gray' }}>
                                                NOTE: DataSets may be linked directly (via Foreign Key) to Charts, Chart DataSets, DataSet Column Formats, DataSet Inputs, DataSet Securities, Drilldowns, Report Items, and more.
                                            </Text>
                                        </div>
                                    </Col>
                                    <Col span={1} />
                                </Row>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={17} />
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='dsApplyButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        htmlType='submit'
                                        style={{width: '95%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                        type='primary'
                                    >
                                        {addButtonClicked ? 'Add DataSet' : (updButtonClicked ? 'Update DataSet' : (delButtonClicked ? 'Delete DataSet' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={3}>
                            <div style={{ textAlign: 'right' }}>
                                <Form.Item
                                    name='dsCancelButton'
                                    style={{ marginBottom: '0px' }}
                                >
                                    <Button
                                        style={{width: '95%'}}
                                        type='default'
                                        onClick={onDSCancel}
                                    >
                                        {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                    </Button>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={1} />
                    </Row>
                </Form>
            </Spin>
        </Card>
    );
};

export default AUDDataSet;
